<template>
    <div class="page-container">
        <div class="grid-controls">
            <div class="grid-controls-row-1">
                <div class="search-controls">
                    <label for="match-algo-name" class="control-margin-r">Name search</label>
                    <select id="match-algo-name" class="match-algo-select" v-model="groupStore.searchType" @change="refreshGrid()">
                        <option
                            v-for="type of Object.keys(GroupSearchType)"
                            :value="GroupSearchType[type]"
                            :key="type"
                        >
                            {{ type }}
                        </option>
                    </select>
                    <input type="text" v-model="groupStore.searchTerm" class="search-text control-margin-r" @input="refreshGrid()">
                    <input type="button" value="Clear" @click="clearSearchName()" class="button button-sm">
                </div>
            </div>
        </div>
        <div class="grid-record-count">
            <div class="record-count-text">{{ recordCount }} records showing</div>
        </div>
        <ag-grid-vue
            style="width: 100%; height: 80%"
            class="ag-theme-quartz"
            :rowData="groupRows"
            :columnDefs="colDefs"
            rowSelection="multiple"
            :rowMultiSelectWithClick="true"
            @grid-ready="onGridReady"
            @rowDoubleClicked="rowClicked"
        />
    </div>
</template>

<script setup>
    import { ref, inject, onBeforeMount, nextTick } from 'vue';
    import 'ag-grid-community/styles/ag-grid.css';
    import 'ag-grid-community/styles/ag-theme-quartz.css';
    import { AgGridVue } from 'ag-grid-vue3';
    import { promptModal } from 'jenesius-vue-modal';
    import { useGroupStore, GroupSearchType } from '@/stores/GroupStore';
    import { useCompanyStore } from '@/stores/CompanyStore';
    import { useSettingsStore } from '@/stores/SettingsStore';
    import EditGroup from '@/components/EditGroup.vue';
    import Error from '@/components/Error.vue';

    const groupStore = useGroupStore();
    const companyStore = useCompanyStore();
    const settingsStore = useSettingsStore();
    const groupRows = ref(groupStore.groups);
    let gridApi = null;
    const recordCount = ref('');

    // Column Definitions: Defines & controls grid columns.
    const colDefs = ref([
        { field: 'id', headerName: 'ID', width: 90, hide: !settingsStore.debugMode },
        { field: 'groupName', headerName: 'Parent name', width: 300 },
        {
            field: 'groupNotes',
            headerName: 'Parent notes',
            flex: 1,
            autoHeight: true,
            wrapText: true,
            cellRenderer: params => `<pre class="company-notes">${params.value}</pre>`
        },
        {
            field: 'companies',
            headerName: 'Divisions',
            flex: 1,
            autoHeight: true,
            cellRenderer: params => {
                const companiesNotes = params.value.map(c => {
                    let notes = c.hasOverride ? c.overrideNotes || '' : c.description || '';
                    return `${c.name}${notes ? '&nbsp;&nbsp;&#8212;&nbsp;&nbsp;' + notes : ''}`;
                });

                return `
                    <div class="scrollable-notes">
                        <ul class="company-list-2">
                            ${companiesNotes.map(cn => `<li class="list-item"><span>${cn}</span></li>`).join('')}
                        </ul>
                    </div>
                `;
            }
        }
    ]);

    async function onGridReady(params) {
        gridApi = params.api;
        await fetchGroups();
    }

    async function fetchGroups() {
        gridApi.showLoadingOverlay();

        try {
            await groupStore.fetchGroups();
        }
        catch (err) {
            promptModal(Error, { message: 'Error retrieving parent company data', details: err.stack });
        }
        finally {
            gridApi.hideOverlay();
        }

        refreshGrid();
    }

    function clearSearchName() {
        groupStore.searchTerm = '';
        refreshGrid();
    }

    function refreshGrid() {
        groupRows.value = groupStore.groups;
        recordCount.value = groupRows.value.length;
    }

    async function rowClicked(event) {
        const data = {
            groupId: event.data.id,
            name: event.data.groupName,
            notes: event.data.groupNotes,
            companies: event.data.companies
        };

        const res = await promptModal(EditGroup, data);

        if (res) {
            fetchGroups();
        }
    }

    companyStore.$onAction(params => {
        if (params.name === 'updateCompanyNotes' || params.name === 'updateCompanyNotes2') {
            params.after(result => {
                // somebody changes a company note (probably in the edit company dialog)
                fetchGroups();
            });
        }
    });

    settingsStore.$onAction(params => {
        if (params.name === 'setDebugMode') {
            params.after(result => {
                gridApi.setColumnVisible('id', settingsStore.debugMode);
            });
        }
    })
</script>

<style scoped>
    .page-container {
        display: flex;
        flex-direction: column;
        height: 100%;
    }
    
    .grid-controls {
        margin-top: 10px;
        margin-bottom: 10px;
    }

    .grid-controls-row-1 {
        display: flex;
        flex-direction: row;
        justify-content: right;
        align-items: baseline;
    }

    .control-margin-r {
        margin-right: 10px;
    }

    .search-controls {
        flex: 3;
        display: flex;
        flex-direction: row;
        justify-content: right;
        align-items: baseline;
    }

    .match-algo-select {
        width: 100px;
        margin-right: 10px;
    }

    .search-text {
        flex-basis: 300px;
    }

    .grid-record-count {
        display: flex;
        flex-direction: row;
        justify-content: right;
        margin: 0px;
        margin-bottom: 10px;
    }

    .record-count-text {
        color: gray;
    }
</style>
@/stores/GroupStore
